import type { Cart, ProductDetail, ProductSummary, PublicOrderStatus } from '@sshop/domain';

import { env } from '@/shared/config/env';
import { api } from '@/shared/lib/apiClient';
import { activityConsentChoice, activityHeaders, activityUuid, clearActivityIdentity, setActivityConsent } from '@/shared/lib/activityIdentity';
import { hashMetaCustomer, metaCookies, type MetaCustomer } from './metaMatching';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void;
      push?: (...args: unknown[]) => void;
      queue?: unknown[][];
      loaded?: boolean;
      version?: string;
    };
    _fbq?: Window['fbq'];
  }
}

type MetaPixelQueue = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  push?: (...args: unknown[]) => void;
  queue?: unknown[][];
  loaded?: boolean;
  version?: string;
};

const CONSENT_KEY = 'sshop_tracking_consent';
let initialized = false;
let metaInitialized = false;
let metaMatchingInitialized = false;
let lastPageView = '';
let lastProductView = '';
let lastCartView = '';
let lastCheckoutView = '';
let metaCustomer: MetaCustomer = {};
const purchasedThisPage = new Set<string>();

export function setMetaCustomer(customer: MetaCustomer) {
  metaCustomer = customer;
}
type ActivityDetails = Record<string, string | number>;
type ActivityEvent = {
  event_id: string; event: string; page?: string; product_id?: number;
  collection_id?: number; details?: ActivityDetails;
  attempts?: number;
};
let activityQueue: ActivityEvent[] = [];
let activityTimer: ReturnType<typeof setTimeout> | null = null;

function flushActivity(keepalive = false) {
  activityTimer = null;
  if (!activityQueue.length || !activityHeaders()['X-Activity-Consent']) {
    activityQueue = [];
    return;
  }
  const batch = activityQueue.splice(0, 20);
  void api.activity.send(batch.map(({ attempts: _attempts, ...event }) => event), keepalive).catch(() => {
    // Analytics must never interrupt shopping. Keep one bounded retry.
    activityQueue = [...batch.filter((event) => !event.attempts).map((event) => ({ ...event, attempts: 1 })), ...activityQueue].slice(0, 40);
    if (!activityTimer) activityTimer = setTimeout(flushActivity, 1500);
  });
  if (activityQueue.length) activityTimer = setTimeout(flushActivity, 300);
}

export function recordActivity(event: string, attributes: Omit<ActivityEvent, 'event_id' | 'event'> = {}) {
  if (!activityHeaders()['X-Activity-Consent']) return;
  try {
    activityQueue.push({ event_id: activityUuid(), event, page: window.location.pathname, ...attributes });
  } catch { return; }
  if (!activityTimer) activityTimer = setTimeout(flushActivity, 300);
}

function push(payload: unknown) {
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(payload);
}

function gtag(...args: unknown[]) {
  push(args);
}

function hasConsent() {
  try {
    return activityConsentChoice() === 'granted';
  } catch {
    return false;
  }
}

function loadMetaPixel() {
  if (!env.META_PIXEL_ID || metaInitialized) return;
  metaInitialized = true;
  const fbq: MetaPixelQueue = (...args: unknown[]) => {
    if (fbq.callMethod) fbq.callMethod(...args);
    else (fbq.queue = fbq.queue ?? []).push(args);
  };
  fbq.push = fbq;
  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = '2.0';
  window.fbq = fbq;
  window._fbq = fbq;
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
  // Commerce events are explicit and paired with CAPI; inferred events have no shared event ID.
  fbq('set', 'autoConfig', false, env.META_PIXEL_ID);
  fbq('init', env.META_PIXEL_ID);
}

export function initializeAnalytics() {
  if (initialized) return;
  initialized = true;
  window.addEventListener('pagehide', () => flushActivity(true));
  window.gtag = gtag;
  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });
  if (env.GTM_ID) {
    push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(env.GTM_ID)}`;
    document.head.appendChild(script);
  }
  if (hasConsent()) setTrackingConsent(true);
}

export function trackingConsentChoice(): 'granted' | 'denied' | null {
  return activityConsentChoice();
}

export function setTrackingConsent(granted: boolean) {
  const wasGranted = hasConsent();
  const value = granted ? 'granted' : 'denied';
  setActivityConsent(granted);
  try {
    window.localStorage.setItem(CONSENT_KEY, value);
    window.localStorage.setItem('cookie_consent', value);
  } catch {
    /* storage may be disabled */
  }
  gtag('consent', 'update', {
    analytics_storage: value,
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  });
  if (granted) {
    if (!wasGranted) {
      lastPageView = '';
      lastProductView = '';
      lastCartView = '';
      lastCheckoutView = '';
    }
    loadMetaPixel();
    window.fbq?.('consent', 'grant');
    if (!wasGranted) window.dispatchEvent(new Event('sshop:analytics-consent-granted'));
  } else {
    activityQueue = [];
    clearActivityIdentity();
    window.fbq?.('consent', 'revoke');
  }
}

function meta(event: string, payload: Record<string, unknown> = {}, eventId?: string, customer = metaCustomer) {
  if (!hasConsent()) return;
  if (Array.isArray(payload['content_ids']) && payload['content_ids'].length === 0) return;
  loadMetaPixel();
  const id = eventId ?? activityUuid();
  const page = window.location.pathname;
  const fields = event === 'ViewContent' ? ['em'] : ['AddToCart', 'InitiateCheckout'].includes(event) ? ['em', 'ph'] : ['em', 'ph', 'fn', 'ln', 'ct'];
  const matching = Object.fromEntries(Object.entries(customer).filter(([key]) => fields.includes(key)));
  void (async () => {
    let userData: Record<string, string> = {};
    try { userData = await hashMetaCustomer(matching); } catch { /* Pixel still works without advanced matching. */ }
    if (!hasConsent()) return;
    if (!metaMatchingInitialized && Object.keys(userData).length) {
      // Meta accepts advanced matching on init while the pixel's matching data is empty.
      // Later field enrichment is sent through CAPI; set/userData is Shopify-only.
      const initialMatching = await hashMetaCustomer(customer).catch(() => userData);
      if (!hasConsent()) return;
      if (!metaMatchingInitialized) {
        metaMatchingInitialized = true;
        window.fbq?.('init', env.META_PIXEL_ID, initialMatching);
      }
    }
    window.fbq?.('track', event, payload, { eventID: id });
    // Registration is emitted by the successful registration handler; Purchase by verified payment/status handlers.
    if (['ViewContent', 'AddToCart', 'InitiateCheckout', 'AddPaymentInfo'].includes(event)) {
      const data = {
        event_name: event, event_id: id, page,
        custom_data: payload, user_data: userData, ...metaCookies(),
      };
      try { await api.meta.send(data); } catch {
        if (hasConsent()) try { await api.meta.send(data); } catch { /* A bounded retry must never block shopping. */ }
      }
    }
  })();
}

function metaItems(items: readonly { item_id: string; item_name: string; price: number; quantity: number; item_brand?: string | undefined; metaCatalog?: ProductSummary['metaCatalog'] }[], value: number) {
  const products = items.map((item) => ({ ...item, item_id: (item.metaCatalog?.content_id ?? item.item_id).trim() }))
    .filter((item) => item.item_id !== '' && !item.item_id.startsWith('collection:'));
  return {
    content_ids: products.map((item) => item.item_id), content_type: 'product', currency: 'GEL', value,
    contents: products.map((item) => ({ id: item.item_id, quantity: item.quantity, item_price: item.price })),
    num_items: products.reduce((sum, item) => sum + item.quantity, 0),
    content_name: products.map((item) => item.item_name).join(', '),
    content_brand: [...new Set(products.map((item) => item.metaCatalog?.content_brand || item.item_brand).filter(Boolean))].join(', '),
    product_type: [...new Set(products.map((item) => item.metaCatalog?.product_type).filter(Boolean))].join(', '),
    content_category: [...new Set(products.map((item) => topCategory(item.metaCatalog?.content_category)).filter(Boolean))].join(', '),
  };
}

function metaCartItems(cart: Cart) {
  return [...cart.items.map((item) => ({ item_id: item.sku, item_name: item.title, price: item.unitPrice.amount, quantity: Number(item.quantity), metaCatalog: item.metaCatalog })),
    ...(cart.collections ?? []).flatMap((set) => {
      const base = set.components.reduce((sum, item) => sum + (item.unitPrice?.amount ?? 0) * item.quantity, 0);
      return set.components.filter((item) => item.sku).map((item) => ({
        item_id: item.sku!, item_name: item.title, quantity: item.quantity * Number(set.quantity),
        metaCatalog: item.metaCatalog,
        price: base > 0 ? Math.round((item.unitPrice?.amount ?? 0) * set.unitPrice.amount / base * 100) / 100 : 0,
      }));
    })];
}

export function trackPageView(path: string) {
  if (lastPageView === path) return;
  lastPageView = path;
  lastProductView = '';
  recordActivity('page_view', { page: path.split('?')[0] ?? '/' });
  push({
    event: 'page_view',
    page_location: new URL(path, window.location.origin).href,
    page_title: document.title,
  });
  meta('PageView');
}

function topCategory(path: string | undefined) {
  return path?.split('/')[0]?.trim() ?? '';
}

function productItem(product: ProductSummary, quantity = 1) {
  return {
    item_id: String(product.sku),
    item_name: product.title,
    item_brand: product.brand ?? undefined,
    price: product.price.amount,
    quantity,
  };
}

function cartItems(cart: Cart) {
  return [...cart.items.map((item) => ({
    item_id: item.sku,
    item_name: item.title,
    price: item.unitPrice.amount,
    quantity: Number(item.quantity),
  })), ...(cart.collections ?? []).map((item) => ({
    item_id: 'collection:' + item.collectionId,
    item_name: item.title,
    price: item.unitPrice.amount,
    quantity: Number(item.quantity),
  }))];
}

export function trackViewItem(product: ProductDetail) {
  const viewKey = `${window.location.pathname}:${product.sku}`;
  if (lastProductView === viewKey) return;
  lastProductView = viewKey;
  recordActivity('product_view', { product_id: Number(product.id) });
  const item = productItem(product);
  push({ ecommerce: null });
  push({
    event: 'view_item',
    ecommerce: { currency: 'GEL', value: product.price.amount, items: [item] },
  });
  if (product.sku) meta('ViewContent', {
    ...metaItems([{ ...item, metaCatalog: product.metaCatalog }], product.price.amount),
    product_type: product.metaCatalog?.product_type ?? (product.categories ?? []).map((category) => category.name).join(' / '),
    content_category: topCategory(product.metaCatalog?.content_category ?? product.categories?.[0]?.name),
  });
}

export function trackAddToCart(product: ProductSummary, quantity: number) {
  const value = product.price.amount * quantity;
  push({ ecommerce: null });
  push({
    event: 'add_to_cart',
    ecommerce: { currency: 'GEL', value, items: [productItem(product, quantity)] },
  });
  if (product.sku) meta('AddToCart', metaItems([{ ...productItem(product, quantity), metaCatalog: product.metaCatalog }], value));
}

export function trackAddCollectionToCart(cart: Cart, collectionId: string, quantity: number) {
  const collection = cart.collections?.find((item) => item.collectionId === collectionId);
  if (!collection) return;
  const contents = metaCartItems({ ...cart, items: [], collections: [{ ...collection, quantity: quantity as typeof collection.quantity }] });
  if (contents.length) meta('AddToCart', metaItems(contents, collection.unitPrice.amount * quantity));
}

export function trackViewCart(cart: Cart) {
  if (lastCartView === cart.updatedAt) return;
  lastCartView = cart.updatedAt;
  recordActivity('cart_view');
  push({ ecommerce: null });
  push({
    event: 'view_cart',
    ecommerce: { currency: 'GEL', value: cart.totals.total.amount, items: cartItems(cart) },
  });
}

export function trackBeginCheckout(cart: Cart, visitKey = `${window.location.pathname}${window.location.search}`) {
  if (!cart.items.length && !cart.collections?.length) return;
  // Cart refetches and route effects can run after checkout has already rendered.
  // The router's history key identifies the visit regardless of cart timestamps.
  if (lastCheckoutView === visitKey) return;
  lastCheckoutView = visitKey;
  recordActivity('checkout_started');
  const items = cartItems(cart);
  push({ ecommerce: null });
  push({
    event: 'begin_checkout',
    ecommerce: { currency: 'GEL', value: cart.totals.total.amount, items },
  });
  const contents = metaCartItems(cart);
  if (contents.length) meta('InitiateCheckout', metaItems(contents, cart.totals.total.amount));
}

export function trackPaymentSelection(cart: Cart, paymentType: string) {
  recordActivity('payment_selected', { details: { payment_type: paymentType } });
  push({ ecommerce: null });
  push({
    event: 'add_payment_info',
    ecommerce: {
      currency: 'GEL',
      value: cart.totals.total.amount,
      payment_type: paymentType,
      items: cartItems(cart),
    },
  });
  const contents = metaCartItems(cart);
  if (['bog', 'bog-card', 'bog-apple-pay', 'bog-google-pay', 'tbc'].includes(paymentType) && contents.length) {
    meta('AddPaymentInfo', metaItems(contents, cart.totals.total.amount));
  }
}

export function trackCompleteRegistration(user: { id: string; email: string; phone: string; firstName: string; surname: string }) {
  setMetaCustomer({ em: user.email, ph: user.phone, fn: user.firstName, ln: user.surname });
  meta('CompleteRegistration', {}, `CompleteRegistration.${user.id}`);
}

export function trackPurchaseOnce(order: PublicOrderStatus) {
  const completed = ['paid', 'processing', 'shipped', 'delivered'].includes(order.status);
  const cashOrderAccepted = order.paymentMethod === 'cod' && order.status === 'pending';
  if (!completed && !cashOrderAccepted) return;
  if (!hasConsent()) return;
  const key = `sshop_purchase_${order.id}`;
  const metaKey = `sshop_meta_purchase_${order.id}`;
  const alreadySent = (id: string) => {
    try { return purchasedThisPage.has(id) || Boolean(window.localStorage.getItem(id)); }
    catch { return purchasedThisPage.has(id); }
  };
  const markSent = (id: string) => {
    purchasedThisPage.add(id);
    try { window.localStorage.setItem(id, new Date().toISOString()); } catch { /* memory fallback */ }
  };
  const items = order.items.map((item) => ({
    item_id: item.itemId,
    item_name: item.itemName,
    price: item.price,
    quantity: item.quantity,
  }));
  if (!alreadySent(key)) {
    push({ ecommerce: null });
    push({
      event: 'purchase',
      ecommerce: {
        transaction_id: order.id,
        currency: order.currency,
        value: order.value,
        shipping: order.shipping,
        items,
      },
    });
    markSent(key);
  }
  const confirmedMetaPurchase = order.metaPurchase ?? (order.metaPurchase === undefined && order.status === 'paid' && order.paymentMethod === 'online'
    ? { ...metaItems(items, order.value), order_id: String(order.id) } : null);
  if (completed && !alreadySent(metaKey) && confirmedMetaPurchase) {
    meta(
      'Purchase',
      confirmedMetaPurchase,
      `Purchase.${order.id}`,
      { em: order.customer.email, ph: order.customer.phone, fn: order.customer.name.split(/\s+/)[0] ?? '',
        ln: order.customer.name.split(/\s+/).slice(1).join(' '), ct: order.delivery.city },
    );
    markSent(metaKey);
  }
}
